import { v4 } from "uuid";

/**
 * Generates an RFC 5545 iCalendar (.ics) file as a string and triggers a
 * browser file download.
 *
 * Uses METHOD:PUBLISH (informational event, no RSVP expected) for maximum
 * compatibility across Google Calendar, Outlook, and Apple Calendar.
 */

type IcsEventOptions = {
  start: string;
  end: string;
  summary: string;
  location: string | null;
  description: string;
};

function toIcsUtcDate(isoDate: string): string {
  return new Date(isoDate)
    .toISOString()
    .replace(/[-:]/g, "")
    .replace(/\.\d+/, "");
}

function foldLine(line: string): string {
  const maxLen = 75;
  if (line.length <= maxLen) return line;

  const parts: string[] = [];
  parts.push(line.slice(0, maxLen));
  let offset = maxLen;

  while (offset < line.length) {
    parts.push(" " + line.slice(offset, offset + maxLen - 1));
    offset += maxLen - 1;
  }

  return parts.join("\r\n");
}

function escapeText(text: string): string {
  return text
    .replace(/\\/g, "\\\\")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,")
    .replace(/\n/g, "\\n");
}

function generateIcsContent(options: IcsEventOptions): string {
  const { start, end, summary, location, description } = options;

  const uid = v4();
  const now = toIcsUtcDate(new Date().toISOString());

  const lines: string[] = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Thalamos//Aspire//EN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `UID:${uid}`,
    `DTSTAMP:${now}`,
    `DTSTART:${toIcsUtcDate(start)}`,
    `DTEND:${toIcsUtcDate(end)}`,
    `SUMMARY:${escapeText(summary)}`,
    `DESCRIPTION:${escapeText(description)}`,
    "CLASS:PRIVATE",
  ];

  if (location) {
    lines.push(`LOCATION:${escapeText(location)}`);
  }

  lines.push("END:VEVENT", "END:VCALENDAR");

  return lines.map(foldLine).join("\r\n") + "\r\n";
}

export function downloadIcsFile(options: IcsEventOptions): void {
  const content = generateIcsContent(options);
  const blob = new Blob([content], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.download = "assessment.ics";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
